import { Layer } from "../_overlay/Layer.js";
import { useAnchoredPosition } from "../_overlay/useAnchoredPosition.js";
/* empty css            */
import { cloneElement, useCallback, useEffect, useId, useRef, useState } from "react";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
//#region src/components/Tooltip/Tooltip.tsx
var HIDE_GRACE_MS = 100;
function refOf(element) {
	if ("ref" in element.props) return element.props.ref;
	return element.ref;
}
function setRef(ref, value) {
	if (typeof ref === "function") ref(value);
	else if (ref) ref.current = value;
}
/** The trigger with Tooltip's props merged in (a component, so its handlers are JSX props). */
function Trigger({ element, ...props }) {
	return cloneElement(element, props);
}
/**
* A short label or description shown next to its trigger on hover and keyboard focus.
* It stays while the pointer is over the trigger or the tip, and Escape hides it
* (WCAG 1.4.13). The trigger is described by it, so screen readers read it on focus.
*/
function Tooltip({ content, placement = "top", children, delay = 120, describes = true, open: openProp, onOpenChange }) {
	const [openState, setOpenState] = useState(false);
	const open = openProp ?? openState;
	const setOpen = useCallback((next) => {
		if (openProp === void 0) setOpenState(next);
		onOpenChange?.(next);
	}, [openProp, onOpenChange]);
	const tipId = `${useId()}-tooltip`;
	const timer = useRef(void 0);
	const hovered = useRef({
		trigger: false,
		tip: false
	});
	const focused = useRef(false);
	const arrowRef = useRef(null);
	const { setAnchor, setFloating, style, side, isPositioned, arrow } = useAnchoredPosition({
		open,
		side: placement,
		offset: 8,
		arrowRef
	});
	const ownRef = refOf(children);
	useEffect(() => () => clearTimeout(timer.current), []);
	const show = (wait) => {
		clearTimeout(timer.current);
		if (open) return;
		timer.current = setTimeout(() => setOpen(true), wait);
	};
	const hideSoon = (wait = HIDE_GRACE_MS) => {
		clearTimeout(timer.current);
		timer.current = setTimeout(() => {
			const { trigger, tip } = hovered.current;
			if (!trigger && !tip && !focused.current && open) setOpen(false);
		}, wait);
	};
	const hideNow = () => {
		clearTimeout(timer.current);
		if (open) setOpen(false);
	};
	const setTrigger = useCallback((element) => {
		setAnchor(element);
		setRef(ownRef, element);
	}, [setAnchor, ownRef]);
	const own = children.props;
	const describedBy = [own["aria-describedby"], describes ? tipId : void 0].filter(Boolean).join(" ") || void 0;
	return /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsx(Trigger, {
		element: children,
		ref: setTrigger,
		"aria-describedby": describedBy,
		onPointerEnter: (event) => {
			own.onPointerEnter?.(event);
			if (event.pointerType === "touch") return;
			hovered.current.trigger = true;
			show(delay);
		},
		onPointerLeave: (event) => {
			own.onPointerLeave?.(event);
			hovered.current.trigger = false;
			if (open) hideSoon();
			else if (!focused.current) clearTimeout(timer.current);
		},
		onPointerDown: (event) => {
			own.onPointerDown?.(event);
			hideNow();
		},
		onFocus: (event) => {
			own.onFocus?.(event);
			focused.current = true;
			if (event.currentTarget.matches(":focus-visible")) show(delay);
		},
		onBlur: (event) => {
			own.onBlur?.(event);
			focused.current = false;
			if (hovered.current.trigger || hovered.current.tip) return;
			hideNow();
		}
	}), open ? /* @__PURE__ */ jsxs(Layer, {
		kind: "tooltip",
		ref: setFloating,
		id: tipId,
		role: "tooltip",
		dismissOnOutsidePress: false,
		restoreFocus: false,
		onDismiss: hideNow,
		className: "ds-tooltip",
		"data-side": side,
		style: {
			...style,
			visibility: isPositioned ? void 0 : "hidden"
		},
		onPointerEnter: () => {
			hovered.current.tip = true;
			clearTimeout(timer.current);
		},
		onPointerLeave: () => {
			hovered.current.tip = false;
			hideSoon();
		},
		children: [content, /* @__PURE__ */ jsx("span", {
			ref: arrowRef,
			className: "ds-tooltip__arrow",
			"aria-hidden": "true",
			style: {
				insetInlineStart: arrow.x === void 0 ? void 0 : `${arrow.x}px`,
				insetBlockStart: arrow.y === void 0 ? void 0 : `${arrow.y}px`
			}
		})]
	}) : describes && /* @__PURE__ */ jsx("span", {
		id: tipId,
		hidden: true,
		children: content
	})] });
}
//#endregion
export { Tooltip };

//# sourceMappingURL=Tooltip.js.map